/* eslint-disable prefer-destructuring */
import {
  EditorState,
  ContentState,
  convertFromRaw,
  convertToRaw,
} from "draft-js";

import uuid from "react-native-uuid";

import HTMLtoDraft from "./HTMLtoDraft/HTMLtoDraft";
import getEditorHTML from "../components/RevvEditor/getEditorHtml";
import { DOCUMENTS, DRAFT, COMMON } from "../constants";

// Internal Imports
import { numberForamting } from "./globalUtils";
import { normaliseTableData } from "./tableUtils";
import { convertSectionRawToDraft } from "./components";

// Destructuring
const { CURRENCY_SYMBOL, DEFAULT_CURRENCY } = COMMON;
const { DOCUMENT_COMPONENT_TYPES } = DOCUMENTS;
/*
Convert the raw state of data to draft state for html
*/

export const convertRawToDraft = data => {
  try {
    if (
      data &&
      typeof data === "object" &&
      DRAFT.DRAFT_RAW_KEYS.length === Object.keys(data).length &&
      JSON.stringify(DRAFT.DRAFT_RAW_KEYS.sort()) ===
        JSON.stringify(Object.keys(data).sort())
    ) {
      return convertFromRaw(data);
    }
    console.error(`Raw to draft conversion error. Raw keys unavailable`);
    const editorState = EditorState.createEmpty();
    return editorState.getCurrentContent();
  } catch (error) {
    throw Error(
      `Unable to convert raw state to draft state. Error: ${error.message}`,
    );
  }
};

export const convertHTMLStringToDraft = (html, mode) => {
  try {
    return HTMLtoDraft(html, mode);
  } catch (error) {
    console.error(
      `Unable to convert the html object into editor object. Error: ${
        error.message
      }`,
    );
    throw Error(
      `Unable to convert the html object into editor object. Error: ${
        error.message
      }`,
    );
  }
};

export const convertDocumentContentV2 = (components, mode) => {
  Object.keys(components).forEach(componentId => {
    let section = components[componentId];
    if (section) {
      section = convertSectionRawToDraft(section.content, mode);
    }
    components[componentId] = {
      content: section,
      dbRowIndex: components[componentId].dbRowIndex,
      dbColIndex: components[componentId].dbColIndex,
    };
  });
  return components;
};

export const createTableRevision = table => {
  const tableData = table.data.map(row => {
    row.data.forEach(col => {
      if (typeof col.data === "object") {
        // reseting diffHtml & diffState of each cell so previous version's diffs don't show in new versions
        if ("diffHtml" in col.data) col.data.diffHtml = "";
        if ("diffState" in col.data) col.data.diffState = {};
      }
    });
    return row;
  });
  return { ...table, data: tableData };
};

export const createComponentListRevision = sectionList =>
  Array.isArray(sectionList)
    ? // eslint-disable-next-line no-use-before-define
      sectionList.map(section => createComponentRevision(section))
    : [];

export const createComponentRevision = section => {
  const newSection = {
    ...section,
  };
  if (section.type === DOCUMENT_COMPONENT_TYPES.TABLE) {
    newSection.data = createTableRevision(section.data);
  } else if (section.type === DOCUMENT_COMPONENT_TYPES.HTML) {
    if (section.currentContent instanceof ContentState) {
      newSection.data = convertToRaw(section.currentContent);
    } else if (section.data instanceof ContentState) {
      newSection.data = convertToRaw(section.data);
    }
    delete newSection.currentContent;
    delete newSection.diffHtml;
    delete newSection.diffState;
  } else if (section.type === DOCUMENT_COMPONENT_TYPES.COVERPAGE) {
    newSection.data = {
      ...section.data,
      data: createComponentListRevision(section.data.data),
    };
  } else if (section.type === DOCUMENT_COMPONENT_TYPES.HEADER_FOOTER) {
    newSection.data = {
      ...section.data,
      footer: {
        ...section.data.footer,
        data: createComponentListRevision(section.data.footer.data),
      },
      header: {
        ...section.data.header,
        data: createComponentListRevision(section.data.header.data),
      },
    };
  }
  return newSection;
};

export const createDocumentRevision = sectionList => {
  const componentList = [];
  const revisionList = createComponentListRevision(sectionList);
  revisionList.forEach(section => {
    // create a new section
    const newSection = {
      section_id: uuid.v4(),
      section_type: section.type,
      section_name: section.label,
      lock: section.lock,
      data: section.data,
      html: section.html,
      clauseID: section.clauseID,
      form_field: section.form_field,
      clauseOptions: section.clauseOptions,
    };
    if (section.type === DOCUMENT_COMPONENT_TYPES.GOOGLE_SHEET) {
      newSection.sheet = section.sheet;
      newSection.workSheet = section.workSheet;
      newSection.rangeValue = section.rangeValue;
      newSection.autoTrim = section.autoTrim;
      newSection.connected = section.connected;
      newSection.firstRowAsHeader = section.firstRowAsHeader;
    }
    // check if the content in an object
    componentList.push({
      component_id: newSection.section_id,
      content: {
        ...newSection,
      },
    });
  });
  return componentList;
};

export const getFieldValue = (field, currency) => {
  if (!currency) {
    currency = DEFAULT_CURRENCY;
  }
  let { value: fieldValue } = field;
  if (!fieldValue) {
    // check if default is there or not
    if (field.meta && field.meta.default) {
      fieldValue = field.meta.default;
    } else {
      return "";
    }
  }

  let value;
  if (field.type === "PERIOD") {
    const { periodValue, periodUom } = fieldValue;
    value = `${periodValue || ""} ${periodUom || ""}`;
  } else if (field.type === "ADDRESS") {
    const {
      addressLineOne,
      addressLineTwo,
      city,
      state,
      zip,
      country,
    } = fieldValue;
    if (
      addressLineOne.length === 0 &&
      addressLineTwo.length === 0 &&
      city.length === 0 &&
      state.length === 0 &&
      zip.length === 0 &&
      country.length === 0
    )
      value = null;
    else
      value = `${addressLineOne}, ${
        addressLineTwo ? `${addressLineTwo},` : ""
      } ${city}, ${state} - ${zip} ${
        country && country.length !== 0 ? `(${country})` : ""
      }`;
  } else if (field.type === "PERSON_NAME") {
    const { firstName, lastName } = fieldValue;
    value = firstName || lastName ? `${firstName} ${lastName}` : "";
  } else if (field.type === "AMOUNT") {
    let currencyInfo = currency;

    if (
      Object.keys(field.meta).length !== 0 &&
      field.meta.currency &&
      field.meta.currency !== "PRICEBOOK"
    ) {
      currencyInfo = field.meta.currency;
    }

    const symbol = CURRENCY_SYMBOL[currencyInfo];
    value = numberForamting(fieldValue, symbol);
  } else if (field.type === "PERCENT") {
    if (fieldValue !== "") {
      value = numberForamting(fieldValue, "", "%");
    } else {
      value = fieldValue;
    }
  } else if (field.type === "RATE") {
    const { rateValue, rateUom } = fieldValue;
    if (rateValue === "" || rateUom === "") {
      value = "";
    } else {
      value = `${rateValue} ${rateUom}`;
    }
  } else if (field.type === "CHECKBOX") {
    value = Array.isArray(fieldValue) ? fieldValue.join(", ") : "";
  } else {
    value = fieldValue;
  }
  return value;
};

export const fillFormFieldinEntity = (section, fieldsUsed, currency) => {
  const { data, currentContent } = section;
  const rawData = currentContent || data;
  if (Array.isArray(fieldsUsed) && fieldsUsed.length > 0) {
    const { entityMap } = rawData;
    Object.keys(entityMap).forEach(entityKey => {
      const entity = entityMap[entityKey];
      if (entity.type === "DYNAMICFIELD") {
        const fieldDesc = fieldsUsed.find(
          f => f.id === entity.data.dynamicFieldId,
        );
        if (fieldDesc && entity.value !== fieldDesc.value) {
          const fieldValue = {
            ...fieldDesc,
            value: fieldDesc.value,
          };
          entityMap[entityKey].data.value = getFieldValue(fieldValue, currency);
        }
      }
    });
  }
  return rawData;
};

export const fillFormField = (sections, mode, fieldsUsed, currency) =>
  sections.map(section => {
    if (section.length > 1) {
      return fillFormField(section, mode, fieldsUsed, currency);
    }
    if (section.content) {
      if (section.content.type === DOCUMENT_COMPONENT_TYPES.COVERPAGE) {
        section.content.data.data = fillFormField(
          section.content.data.data,
          mode,
          fieldsUsed,
          currency,
        );
        return section;
      }
      if (section.content.type === DOCUMENT_COMPONENT_TYPES.HEADER_FOOTER) {
        section.content.data.header.data = fillFormField(
          section.content.data.header.data,
          mode,
          fieldsUsed,
          currency,
        );
        section.content.data.footer.data = fillFormField(
          section.content.data.footer.data,
          mode,
          fieldsUsed,
          currency,
        );
        return section;
      }
    }
    let content = null;
    let sectionType = null;
    let type = null;
    let data = null;
    if (section.content) {
      // const { content } = section;
      // const { section_type: sectionType, type, data } = content;
      content = section.content;
      sectionType = content.section_type;
      type = content.type;
      data = content.data;
    } else if (section[0]) {
      content = section[0];
      sectionType = content.type;
      type = content.type;
      data = content.data;
    } else {
      content = section;
      sectionType = content.type;
      type = content.type;
      data = content.data;
    }
    if (
      (type === "HTML" || sectionType === "HTML") &&
      typeof data !== "string"
    ) {
      if (section.content) {
        section.content.currentContent = fillFormFieldinEntity(
          content,
          fieldsUsed,
          currency,
        );
        section.content.html = getEditorHTML(
          section.content.currentContent,
          mode,
        );
      } else if (section[0]) {
        section[0].currentContent = fillFormFieldinEntity(
          content,
          fieldsUsed,
          currency,
        );
        section[0].html = getEditorHTML(section[0].currentContent, mode);
      } else {
        section.currentContent = fillFormFieldinEntity(
          content,
          fieldsUsed,
          currency,
        );
        section.html = getEditorHTML(section.currentContent, mode);
      }
    }
    if (type === "Table" || sectionType === "Table") {
      const nData = normaliseTableData(data);
      let { data: tableData } = nData;
      tableData = tableData.map(row => {
        let { data: rowData } = row;
        rowData = rowData.map(cell => {
          const { data: cellData } = cell;
          if (typeof cellData === "string") {
            return cell;
          }
          if (typeof cellData === "object") {
            const contentData = {
              data: cellData.data,
              form_field: content.form_field,
              currentContent: cellData.currentContent,
            };
            cellData.currentContent = fillFormFieldinEntity(
              contentData,
              fieldsUsed,
              currency,
            );
            cellData.html = getEditorHTML(cellData.currentContent, mode);
          }
          return cell;
        });
        row.data = rowData;
        return row;
      });
      data.data = tableData;
    }
    return section;
  });

export const fillFormFieldV2 = (components, mode, fieldsUsed, currency) => {
  const filledComponents = {};
  Object.keys(components).forEach(componentId => {
    const section = components[componentId];
    if (section && section.content) {
      const updatedComponent = {
        ...section,
      };
      if (section.content.type === DOCUMENT_COMPONENT_TYPES.COVERPAGE) {
        updatedComponent.content.data.components = fillFormFieldV2(
          section.content.data.components,
          mode,
          fieldsUsed,
          currency,
        );
      } else if (
        section.content.type === DOCUMENT_COMPONENT_TYPES.HEADER_FOOTER
      ) {
        updatedComponent.content.data.header.components = fillFormFieldV2(
          section.content.data.header.components,
          mode,
          fieldsUsed,
          currency,
        );
        updatedComponent.content.data.footer.components = fillFormFieldV2(
          section.content.data.footer.components,
          mode,
          fieldsUsed,
          currency,
        );
      } else if (section.content.type === DOCUMENT_COMPONENT_TYPES.HTML) {
        if (typeof section.content.data !== "string") {
          updatedComponent.content.currentContent = fillFormFieldinEntity(
            section.content,
            fieldsUsed,
            currency,
          );
          updatedComponent.content.html = getEditorHTML(
            updatedComponent.content.currentContent,
            mode,
          );
        }
      } else if (section.content.type === DOCUMENT_COMPONENT_TYPES.TABLE) {
        const nData = normaliseTableData(section.content.data);
        let { data: tableData } = nData;
        tableData = tableData.map(row => {
          let { data: rowData } = row;
          rowData = rowData.map(cell => {
            const { data: cellData } = cell;
            if (typeof cellData === "string") {
              return cell;
            }
            if (typeof cellData === "object") {
              const contentData = {
                data: cellData.data,
                form_field: section.content.form_field,
                currentContent: cellData.currentContent,
              };
              cellData.currentContent = fillFormFieldinEntity(
                contentData,
                fieldsUsed,
                currency,
              );
              cellData.html = getEditorHTML(cellData.currentContent, mode);
            }
            return cell;
          });
          row.data = rowData;
          return row;
        });
        updatedComponent.content.data.data = tableData;
      }
      filledComponents[componentId] = updatedComponent;
    }
  });
  return filledComponents;
};

export const getComponentList = layout => {
  let componentList = [];
  layout.forEach(rowList => {
    if (typeof rowList === "string") componentList.push(rowList);
    else if (Array.isArray(rowList))
      componentList = [...componentList, ...rowList];
  });
  return componentList;
};

export const getOrderedSignersList = (signatories, layout) => {
  let signersList = [];
  if (Array.isArray(layout) && layout.length !== 0) {
    const componentList = getComponentList(layout);
    componentList.forEach(componentId => {
      const signatureBlock = signatories.find(
        sig => sig.component_id === componentId,
      );
      if (signatureBlock) {
        const { signers } = signatureBlock;
        if (Array.isArray(signers) && signers.length > 0) {
          signers.forEach(signer => {
            if (
              signer.signer_email !== undefined &&
              signersList.find(s => s.signer_email === signer.signer_email)
            ) {
              const index = signersList.findIndex(
                s => s.signer_email === signer.signer_email,
              );
              signersList[index].occurences = [
                ...signersList[index].occurences,
                componentId,
              ];
            } else {
              signersList.push({
                ...signer,
                occurences: [componentId],
              });
            }
          });
        }
      }
    });
  }

  if (!Array.isArray(layout) || layout.length === 0) {
    // this is for pdf document
    // const signersList = signatories.sort((signer1, signer2) => {
    //   const { point: point1 } = signer1.signers.signer_config.point;
    //   const { point: point2 } = signer2.signers.signer_config.point;
    //   // check page number
    //   if (point1.page !== point2.page) {
    //     return point1.page - point2.page;
    //   }
    //   return point1.y !== point2.y
    //     ? point1.y - point2.y
    //     : point1.x - point2.x;
    // });

    // return signersList;
    const signersDict = {};
    signatories.forEach(signatureBlock => {
      const { component_id: componentId, signers } = signatureBlock;
      if (Array.isArray(signers) && signers.length > 0) {
        signers.forEach(signer => {
          if (
            signer.signer_email !== undefined &&
            signersDict[signer.signer_email]
          ) {
            signersDict[signer.signer_email] = {
              ...signersDict[signer.signer_email],
              occurences: [
                ...signersDict[signer.signer_email].occurences,
                componentId,
              ],
            };
          } else {
            signersDict[signer.signer_email] = {
              ...signer,
              occurences: [componentId],
            };
          }
        });
      }
    });
    signersList = Object.values(signersDict);
  }

  const signers = [];
  if (signersList.length > 0) {
    signersList.forEach((signer, i) => {
      signers.push({
        user_email: signer.signer_email,
        first_name: signer.first_name,
        last_name: signer.last_name,
        recipientId: i + 1,
        mobile_number: signer.mobile_number,
        auth_type: signer.auth_type,
      });
    });
  }
  return signers;
};



// WEBPACK FOOTER //
// ./src/utils/quotationUtils.js